import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Select from '../../common/Select';
import Button from '../../common/Button';
import MultiSelectScrollable from '../../common/MultiSelectScrollable';
import SearchableMultiSelect from '../../common/SearchableMultiSelect';
import { validateRequired, validateEmail, validatePhone } from '../../../utils/validators';
import customerService from '../../../services/customerService';
import siteService from '../../../services/siteService';

const AddSpocModal = ({ isOpen, onClose, onSubmit }) => {
  const initialState = {
    name: '',
    designation: '',
    email: '',
    contact: '',
    password: '',
    company_id: '',
    site_ids: []
  };

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCompanies();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.company_id) {
      loadSites(formData.company_id);
    } else {
      setSites([]); 
    }
  }, [formData.company_id]);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    const result = await customerService.getCustomerCompanies();
    if (result.success) setCompanies(result.data);
    setLoadingCompanies(false);
  };

  const loadSites = async (companyId) => {
    setLoadingSites(true);
    const result = await siteService.getAllSites();
    if (result.success) {
      const selectedCompanyId = parseInt(companyId, 10);
      const filteredSites = result.data.filter((site) => site.company_id === selectedCompanyId);
      setSites(filteredSites);
    }
    setLoadingSites(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updatedData = { ...prev, [name]: value };
      if (name === 'company_id') {
        updatedData.site_ids = []; 
      }
      return updatedData;
    });

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSitesChange = (newSiteIds) => {
    setFormData(prev => ({ ...prev, site_ids: newSiteIds }));
    if (errors.site_ids) {
      setErrors(prev => ({ ...prev, site_ids: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!validateRequired(formData.name)) newErrors.name = 'Name is required';
    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!validateRequired(formData.contact)) {
      newErrors.contact = 'Contact number is required';
    } else if (!validatePhone(formData.contact)) {
      newErrors.contact = 'Please enter a valid 10-digit phone number';
    }
    if (!validateRequired(formData.password)) newErrors.password = 'Password is required';
    if (!validateRequired(formData.company_id)) newErrors.company_id = 'Company is required';
    if (formData.site_ids.length === 0) newErrors.site_ids = 'At least one site must be selected';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        company_id: parseInt(formData.company_id),
        site_ids: formData.site_ids.map(id => parseInt(id))
      };
      
      await onSubmit(payload);
      setFormData(initialState);
      onClose();
    } catch (error) {
      console.error('Error creating SPOC:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData(initialState);
      setErrors({});
      onClose();
    }
  };

  const companyOptions = [
    { value: '', label: '-- Select Company --' },
    ...companies.map(c => ({ value: c.id.toString(), label: c.company_name }))
  ];

  const siteOptions = sites.map(s => ({ 
    value: s.id.toString(), 
    label: s.name || s.site_name,
    subtitle: s.location || (companies.find(c => c.id === s.company_id)?.company_name)
  }));

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Adding...' : 'Add SPOC'}
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New SPOC" size="medium" footer={footer}>
      <form onSubmit={handleSubmit}>
        <Input label="Full Name" name="name" placeholder="e.g., Jane Doe" value={formData.name} onChange={handleChange} error={errors.name} required />
        <Input label="Designation" name="designation" placeholder="e.g., Facility Manager" value={formData.designation} onChange={handleChange} error={errors.designation} />
        <div style={gridStyle}>
          <Input label="Email" name="email" type="email" placeholder="jane@clientcorp.com" value={formData.email} onChange={handleChange} error={errors.email} required />
          <Input label="Phone Number" name="contact" placeholder="9876543210" value={formData.contact} onChange={handleChange} error={errors.contact} required />
        </div>

        <div style={gridStyle}>
          <Select
            label="Company"
            name="company_id"
            value={formData.company_id}
            onChange={handleChange}
            options={companyOptions}
            error={errors.company_id}
            disabled={loadingCompanies}
            required
          />
        </div>

        <SearchableMultiSelect
          label="Assigned Sites"
          options={siteOptions}
          selectedValues={formData.site_ids}
          onChange={handleSitesChange}
          error={errors.site_ids}
          loading={loadingSites}
          placeholder="Search sites by name or location..."
          emptyMessage={!formData.company_id ? "Please select a company first to view available sites." : "No sites found for this company."}
          required
        />

        <Input label="Password" name="password" type="password" canPeekPassword placeholder="Create a temporary password" value={formData.password} onChange={handleChange} error={errors.password} required />
      </form>
    </Modal>
  );
};

export default AddSpocModal;